import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Input } from "shared/ui/input"
import { Button } from "shared/ui/button"
import { Label } from "shared/ui/label"
import { Alert, AlertDescription } from "shared/ui/alert"
import { ArrowLeft, KeyRound, CheckCircle, AlertTriangle } from "lucide-react"
import { confirmContractorPasswordReset } from "modules/auth/api/authService"

export function ContractorResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") ?? ""

  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [message, setMessage] = useState("")

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError("")
    setMessage("")

    if (!token) {
      setError("ลิงก์รีเซ็ตไม่ถูกต้อง")
      return
    }
    if (password.length < 8) {
      setError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร")
      return
    }
    if (password !== confirmPassword) {
      setError("รหัสผ่านไม่ตรงกัน")
      return
    }

    setIsLoading(true)
    try {
      const response = await confirmContractorPasswordReset({
        token,
        otp: otp.trim(),
        newPassword: password,
        confirmPassword,
      })
      setMessage(response.message || "รีเซ็ตรหัสผ่านสำเร็จแล้ว")
    } catch (err: any) {
      console.error(err)
      const errorMessage = err?.response?.data ?? "ไม่สามารถรีเซ็ตรหัสผ่านได้"
      setError(typeof errorMessage === "string" ? errorMessage : "ไม่สามารถรีเซ็ตรหัสผ่านได้")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-100 flex items-center justify-center p-6">
      <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate("/login/contractor")}
          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 self-start"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          กลับไปหน้าเข้าสู่ระบบ
        </Button>

        <Card className="shadow-xl border-0 bg-white/95 backdrop-blur w-full">
          <CardHeader className="pb-4 text-center space-y-2">
            <CardTitle className="flex items-center justify-center gap-2 text-2xl text-orange-900">
              <KeyRound className="w-6 h-6 text-orange-500" />
              รีเซ็ตรหัสผ่านผู้รับเหมา
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {error ? (
              <Alert variant="destructive" className="border-red-200 bg-red-50">
                <AlertTriangle className="w-4 h-4" />
                <AlertDescription className="text-red-700">{error}</AlertDescription>
              </Alert>
            ) : null}

            {message ? (
              <Alert className="border-emerald-200 bg-emerald-50">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700">{message}</AlertDescription>
              </Alert>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">รหัส 6 หลัก</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="กรอกรหัสยืนยัน"
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">รหัสผ่านใหม่</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="อย่างน้อย 8 ตัวอักษร"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">ยืนยันรหัสผ่านใหม่</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="กรอกรหัสผ่านอีกครั้ง"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="bg-orange-50/60 border-orange-200 focus-visible:ring-orange-400"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-lg"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>กำลังบันทึก...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>ยืนยันรีเซ็ตรหัสผ่าน</span>
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

